import React from 'react';
import { styled as styled2 } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import type { ProjectContext } from '@atlassian/jira-providers-project-context';
import type { main_softwareCodePage_Repositories$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_Repositories.graphql';
import {
	REPOSITORIES_ERROR_STATE_TEST_ID,
	REPOSITORIES_HEADER_ID,
} from '../../../../common/constants';
import ErrorState from '../../../../common/ui/error-state';
import MoreMenu from '../../../../common/ui/more-menu';
import { useRetryableError } from '../../../../controllers/hooks';
import {
	ViewCodePageExperienceSuccessTracker,
	VIEW_REPO_LIST_EXPERIENCE_NAME,
	useViewCodePageExperienceFail,
} from '../../../../services/experience-tracker';
import messages from './messages';
import RepositoriesList from './repository-list';

type RepositoriesProps = {
	repositoriesData: main_softwareCodePage_Repositories$key;
	onRefetch: () => void;
	isAdmin: boolean;
	projectContext: ProjectContext;
};

export const Repositories = ({
	repositoriesData,
	onRefetch,
	isAdmin,
	projectContext,
}: RepositoriesProps) => {
	const { formatMessage } = useIntl();
	const onRepositoriesExperienceFail = useViewCodePageExperienceFail({
		experienceName: VIEW_REPO_LIST_EXPERIENCE_NAME,
	});
	const { onError, onRetry, caughtError } = useRetryableError();

	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_Repositories on Query {
				...main_softwareCodePage_RepositoriesList
				devOps @required(action: THROW) {
					providers(id: $siteAri, providerTypes: [DEV_INFO])
						@required(action: THROW)
						@optIn(to: "DevOpsSecurityInJira") {
						...main_softwareCodePage_MoreMenu
					}
				}
			}
		`,
		repositoriesData,
	);
	const wrappedOnRefetch = () => {
		onRefetch();
		onRetry();
	};

	return (
		<>
			<Heading size="medium" as="h2">
				<TitleContainer
					id={REPOSITORIES_HEADER_ID}
					data-testid="software-code-page.ui.pages.main-page.repositories.title-container"
				>
					{formatMessage(messages.repositoryListTitle)}
					{ff('platform.atlassian.griffin-navigation-redesign') && (
						<Box xcss={moreMenuWrapperStyles}>
							<MoreMenu
								projectContext={projectContext}
								moreMenuData={data.devOps?.providers ?? null}
								isAdmin={isAdmin}
							/>
						</Box>
					)}
				</TitleContainer>
			</Heading>
			<DescriptionContainer>
				<DescriptionText data-testid="software-code-page.ui.pages.main-page.repositories.description-text">
					{formatMessage(messages.repositoryListDescription)}
				</DescriptionText>
			</DescriptionContainer>
			<ListWrapper>
				<JSErrorBoundary
					teamName="fusion-isotopes"
					packageName="jiraSoftwareCodePage"
					id="jiraSoftwareCodePageRepositoryList"
					onError={(location, error, errorAttributes) => {
						onError(error);
						onRepositoriesExperienceFail(location, error, errorAttributes);
					}}
					fallback={() => (
						<ErrorState
							testId={REPOSITORIES_ERROR_STATE_TEST_ID}
							onRefetch={wrappedOnRefetch}
							description={formatMessage(messages.repositoriesErrorStateTitle)}
						/>
					)}
					resetCaughtError={caughtError}
					sendToPrivacyUnsafeSplunk
				>
					<RepositoriesList repositoriesListData={data} isAdmin={isAdmin} />
					<ViewCodePageExperienceSuccessTracker experienceName={VIEW_REPO_LIST_EXPERIENCE_NAME} />
				</JSErrorBoundary>
			</ListWrapper>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled2.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionText = styled2.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListWrapper = styled2.div({
	padding: token('space.200', '16px'),
	marginTop: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N20),
	borderRadius: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleContainer = styled2.div({
	margin: `0 0 ${token('space.050', '4px')} 0`,
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	height: token('space.400', '24px'),
});

const moreMenuWrapperStyles = xcss({
	fontSize: token('font.size.100'),
});
